import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type UserRole = 'owner' | 'agent' | 'buyer' | null;

export default function SignIn() {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<UserRole>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const roles = [
    {
      id: 'owner' as const,
      title: 'Property Owner',
      description: 'Manage and list your properties',
      icon: 'ri-home-4-line'
    },
    {
      id: 'agent' as const,
      title: 'Agent',
      description: 'Connect buyers with properties',
      icon: 'ri-user-star-line'
    },
    {
      id: 'buyer' as const,
      title: 'Buyer',
      description: 'Find your perfect property',
      icon: 'ri-search-line'
    }
  ];

  const handleSignIn = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole || !email || !password) return;
    // Handle sign in logic here
    console.log('Sign in:', { role: selectedRole, email });
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#EAF0FF' }}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(30, 79, 255, 0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 py-6">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between">
            <img 
              src="https://public.readdy.ai/ai/img_res/21cdb0ce-06f2-4f24-a879-a7c95c269052.png" 
              alt="FlexDown" 
              className="h-8 w-auto cursor-pointer"
              onClick={() => navigate('/')}
            />
            <button
              onClick={() => navigate('/')}
              className="text-sm font-medium text-gray-700 hover:text-[#1E4FFF] transition-colors cursor-pointer whitespace-nowrap"
            >
              Back to Home
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-6 py-12 lg:py-20">
        <div className="max-w-5xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#0B1C2D' }}>
              Welcome Back
            </h1>
            <p className="text-lg" style={{ color: '#6B7A99' }}>
              Sign in to continue managing your real estate journey
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Role Selection */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold mb-6" style={{ color: '#0B1C2D' }}>
                I am a...
              </h2>
              
              <div className="space-y-4">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => setSelectedRole(role.id)}
                    className="w-full text-left p-6 rounded-2xl transition-all duration-300 cursor-pointer group"
                    style={{
                      backgroundColor: selectedRole === role.id ? 'rgba(30, 79, 255, 0.1)' : 'rgba(255, 255, 255, 0.6)',
                      backdropFilter: 'blur(12px)',
                      border: selectedRole === role.id ? '2px solid #1E4FFF' : '2px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: selectedRole === role.id 
                        ? '0 8px 32px rgba(30, 79, 255, 0.15)' 
                        : '0 4px 16px rgba(0, 0, 0, 0.04)'
                    }}
                  >
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300"
                        style={{
                          backgroundColor: selectedRole === role.id ? '#1E4FFF' : 'rgba(30, 79, 255, 0.1)'
                        }}
                      >
                        <i 
                          className={`${role.icon} text-2xl`}
                          style={{ color: selectedRole === role.id ? '#FFFFFF' : '#1E4FFF' }}
                        ></i>
                      </div>
                      <div className="flex-1">
                        <h3 
                          className="text-lg font-semibold mb-1 transition-colors"
                          style={{ color: selectedRole === role.id ? '#1E4FFF' : '#0B1C2D' }}
                        >
                          {role.title}
                        </h3>
                        <p className="text-sm" style={{ color: '#6B7A99' }}>
                          {role.description}
                        </p>
                      </div>
                      <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all"
                        style={{
                          borderColor: selectedRole === role.id ? '#1E4FFF' : '#D1D5DB',
                          backgroundColor: selectedRole === role.id ? '#1E4FFF' : 'transparent'
                        }}
                      >
                        {selectedRole === role.id && (
                          <i className="ri-check-line text-white text-sm"></i>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Sign In Form */}
            <div 
              className="p-8 rounded-2xl"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)'
              }}
            >
              <h2 className="text-2xl font-semibold mb-6" style={{ color: '#0B1C2D' }}>
                Sign In
              </h2>

              <form onSubmit={handleSignIn} className="space-y-5">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0B1C2D' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full px-4 py-3 rounded-xl text-sm transition-all duration-300 focus:outline-none"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(30, 79, 255, 0.2)',
                      color: '#0B1C2D'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1E4FFF';
                      e.target.style.boxShadow = '0 0 0 3px rgba(30, 79, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 79, 255, 0.2)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0B1C2D' }}>
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      className="w-full px-4 py-3 rounded-xl text-sm transition-all duration-300 focus:outline-none pr-12"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        border: '1px solid rgba(30, 79, 255, 0.2)',
                        color: '#0B1C2D'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#1E4FFF';
                        e.target.style.boxShadow = '0 0 0 3px rgba(30, 79, 255, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(30, 79, 255, 0.2)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
                      style={{ color: '#6B7A99' }}
                    >
                      <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
                    </button>
                  </div>
                </div>

                {/* Remember & Forgot */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="w-4 h-4 rounded cursor-pointer"
                      style={{ accentColor: '#1E4FFF' }}
                    />
                    <span className="text-sm" style={{ color: '#6B7A99' }}>
                      Remember me
                    </span>
                  </label>
                  <button
                    type="button"
                    className="text-sm font-medium cursor-pointer whitespace-nowrap"
                    style={{ color: '#1E4FFF' }}
                  >
                    Forgot Password?
                  </button>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!selectedRole}
                  className="w-full py-3.5 rounded-xl text-sm font-semibold text-white transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  style={{
                    backgroundColor: selectedRole ? '#1E4FFF' : '#6B7A99'
                  }}
                >
                  {selectedRole ? 'Sign In' : 'Select a Role to Continue'}
                </button>

                {/* Divider */}
                <div className="relative py-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" style={{ borderColor: 'rgba(107, 122, 153, 0.2)' }}></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 text-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#6B7A99' }}>
                      or continue with
                    </span>
                  </div>
                </div>

                {/* Social Sign In */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className="py-3 rounded-xl text-sm font-medium transition-all duration-300 hover:shadow-lg cursor-pointer whitespace-nowrap"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(30, 79, 255, 0.2)',
                      color: '#0B1C2D'
                    }}
                  >
                    <i className="ri-google-fill mr-2" style={{ color: '#1E4FFF' }}></i>
                    Google
                  </button>
                  <button
                    type="button"
                    className="py-3 rounded-xl text-sm font-medium transition-all duration-300 hover:shadow-lg cursor-pointer whitespace-nowrap"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(30, 79, 255, 0.2)',
                      color: '#0B1C2D'
                    }}
                  >
                    <i className="ri-facebook-fill mr-2" style={{ color: '#1E4FFF' }}></i>
                    Facebook
                  </button>
                </div>

                {/* Sign Up Link */}
                <p className="text-center text-sm pt-4" style={{ color: '#6B7A99' }}>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    className="font-semibold cursor-pointer whitespace-nowrap"
                    style={{ color: '#1E4FFF' }}
                  >
                    Sign Up
                  </button>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
